import React from 'react';
import dayjs from 'dayjs';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const formatDate = (period: string) => {
  if (!period) return '';

  const formatShort = (dateStr: string) =>
    new Date(dateStr.trim()).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
    });

  const formatFull = (dateStr: string) => {
    const date = new Date(dateStr.trim());
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (period.includes('–')) {
    const [start, end] = period.split('–').map(p => p.trim());
    return `${formatFull(start)} – ${formatFull(end)}`;
  }

  return formatShort(period);
};

const getTickFormatter = (viewMode: string, data: any[]) => {
  return (period: string) => {
    const item = data.find((d) => d.period === period);
    if (!item) return period;

    if (viewMode === 'week') {
      return `Week ${item.weekNumber ?? ''}`;
    } else if (viewMode === 'month') {
      return item.monthName ?? '';
    } else {
      return dayjs(item.period).format('DD/MM/YYYY');
    }
  };
};

type CaseLineChartProps = {
  chartsData?: any[];
  viewMode: 'week' | 'month' | 'day';
};

const CaseLineChart: React.FC<CaseLineChartProps> = ({
  chartsData = [],
  viewMode,
}) => {
  // Ensure chartsData is always an array
  const valuesArray = Array.isArray(chartsData)
    ? chartsData.flatMap((d) => [
        d?.job_count || 0,
        d?.completed_count || 0,
        d?.provisional_count || 0
      ])
    : [];

  const maxValue = Math.max(...valuesArray);
  const roundedMax = Math.ceil((maxValue + 1) / 5) * 5;

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={chartsData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="period"
          tickFormatter={getTickFormatter(viewMode, chartsData)}
          // tick={{ angle: -45, textAnchor: 'end', dy: 10 }}
        />
        <YAxis
          domain={[0, roundedMax]}
          allowDecimals={false}
          tickCount={6}
        />
        <Tooltip labelFormatter={formatDate} />
        <Legend />
        <Line
          type="monotone"
          dataKey="job_count"
          stroke="#8884d8"
          name="Job Count"
          dot={{ r: 3 }}
          connectNulls
        />
        <Line
          type="monotone"
          dataKey="completed_count"
          stroke="#82ca9d"
          name="Completed Count"
          dot={{ r: 3 }}
          connectNulls
        />
        <Line
          type="monotone"
          dataKey="provisional_count"
          stroke="#ffc658"
          name="Provisional Count"
          dot={{ r: 3 }}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default CaseLineChart;
